import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import StoreCarousel from './StoreCarousel';
import './BienestarPage.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const PUBLIC = process.env.PUBLIC_URL || '';

const YOGA_PROGRAMS = [
  {
    id: 'equilibrio',
    titleKey: 'programEquilibrioTitle',
    badgeKey: 'programEquilibrioBadge',
    descKey: 'programEquilibrioDesc',
    priceKey: 'programEquilibrioPrice',
    fullDesc:
      'Serie de 5 clases enfocadas en equilibrios de pie, transiciones dinámicas y preparación para equilibrios de brazos (Bakasana/Cuervo). Incluye: enraizamiento y Drishti, equilibrio sobre una pierna, equilibrio dinámico y transiciones, introducción a Bakasana, integración y equilibrios avanzados.',
    philosophy: 'La quietud en el movimiento. La estabilidad nace de la atención plena.',
  },
  {
    id: 'inversiones',
    titleKey: 'programInversionesTitle',
    badgeKey: 'programInversionesBadge',
    descKey: 'programInversionesDesc',
    priceKey: 'programInversionesPrice',
    fullDesc:
      'Serie de 7 clases para una preparación segura hacia las inversiones. Incluye: cimientos de cintura escapular y muñecas, núcleo de poder (core), Viparita Karani, preparación para Sirsasana, práctica con pared, preparación para parada de manos e integración.',
    philosophy: 'Ver el mundo desde otra perspectiva. La seguridad es la prioridad absoluta.',
  },
];

const BienestarPage = () => {
  const { t } = useTranslation();
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [interestEmail, setInterestEmail] = useState('');
  const [interestSubmitting, setInterestSubmitting] = useState(false);
  const [interestSuccess, setInterestSuccess] = useState(false);
  const [interestError, setInterestError] = useState(null);
  const [communityEmail, setCommunityEmail] = useState('');
  const [communitySubmitting, setCommunitySubmitting] = useState(false);
  const [communitySuccess, setCommunitySuccess] = useState(false);
  const [communityError, setCommunityError] = useState(null);

  const scrollToHash = useCallback(() => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    scrollToHash();
    const onHash = () => scrollToHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [scrollToHash]);

  const go = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const scrollToId = (id) => {
    window.history.replaceState({}, '', `/bienestar#${id}`);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goContact = (topic) => {
    go(`/contacto?servicio=${encodeURIComponent(topic)}`);
  };

  const carouselSlides = useMemo(() => [
    {
      type: 'image',
      src: `${PUBLIC}/images/bienestar-carousel-yoga-1a1.png`,
      fallbackSrc: `${PUBLIC}/images/bienestar-carousel-yoga-meditacion.png`,
      subtitle: t('bienestarHub.carouselYogaKicker'),
      title: t('bienestarHub.carouselYogaTitle'),
      description: t('bienestarHub.carouselYogaDesc'),
      cta: { text: t('bienestarHub.carouselYogaCta'), onClick: () => scrollToId('sesiones-a-tu-medida') },
    },
    {
      type: 'image',
      src: `${PUBLIC}/images/bienestar-carousel-entrenamiento-1a1.png`,
      fallbackSrc: `${PUBLIC}/images/bienestar-carousel-talleres-crecimiento.png`,
      subtitle: t('bienestarHub.carouselTrainingKicker'),
      title: t('bienestarHub.carouselTrainingTitle'),
      description: t('bienestarHub.carouselTrainingDesc'),
      cta: { text: t('bienestarHub.carouselTrainingCta'), onClick: () => scrollToId('sesiones-a-tu-medida') },
    },
    {
      type: 'image',
      src: `${PUBLIC}/images/bienestar-carousel-conecta-acompanamiento.png`,
      fallbackSrc: `${PUBLIC}/images/bienestar-carousel-conecta-esencia.png`,
      subtitle: t('bienestarHub.carouselConectaKicker'),
      title: t('bienestarHub.carouselConectaTitle'),
      description: t('bienestarHub.carouselConectaDesc'),
      cta: { text: t('bienestarHub.carouselConectaCta'), onClick: () => scrollToId('programa-conecta') },
    },
  ], [t]);

  const conectaSessions = useMemo(() => (
    [1, 2, 3, 4, 5, 6, 7].map((n) => ({
      n: String(n).padStart(2, '0'),
      title: t(`bienestarHub.conectaSession${String(n).padStart(2, '0')}`),
      desc: t(`bienestarHub.conectaSession${String(n).padStart(2, '0')}Desc`),
    }))
  ), [t]);

  const submitInterest = async (e, courseId, email, setters) => {
    e.preventDefault();
    if (!email.trim()) return;
    setters.setSubmitting(true);
    setters.setError(null);
    try {
      const res = await fetch(`${API_BASE}/bienestar/interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), course: courseId }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setters.setSuccess(true);
        setters.setEmail('');
      } else {
        setters.setError(data.message || 'Error al enviar.');
      }
    } catch {
      setters.setError('Error de conexión.');
    } finally {
      setters.setSubmitting(false);
    }
  };

  return (
    <div className="bienestar-page">
      <Header />

      <StoreCarousel slides={carouselSlides} autoplayInterval={7000} height="520px" />

      <main className="bienestar-main bw-main">
        <div className="container bw-container">

          <aside className="bw-store-note">
            <p>{t('bienestarHub.introNote')}</p>
            <button type="button" className="bw-link-btn" onClick={() => go('/tienda')}>
              {t('bienestarHub.introNoteCta')} <i className="fas fa-arrow-right" aria-hidden="true" />
            </button>
          </aside>

          {/* Sesiones 1 a 1 */}
          <section id="sesiones-a-tu-medida" className="bw-section">
            <header className="bw-section-header">
              <h2 className="bw-section-title">{t('bienestarHub.sessionsTitle')}</h2>
              <p className="bw-section-sub">{t('bienestarHub.sessionsSub')}</p>
            </header>

            <div className="bw-session-grid">
              <article className="bw-session-card bw-session-card--yoga">
                <span className="bw-session-tag">Yoga</span>
                <h3>{t('bienestarHub.yogaCardTitle')}</h3>
                <p className="bw-session-desc">{t('bienestarHub.yogaCardDesc')}</p>
                <ul className="bw-session-meta">
                  <li><i className="fas fa-clock" /> {t('bienestarHub.yogaCardMeta1')}</li>
                  <li><i className="fas fa-calendar-day" /> {t('bienestarHub.yogaCardMeta2')}</li>
                  <li><i className="fas fa-video" /> {t('bienestarHub.yogaCardMeta3')}</li>
                </ul>
                <div className="bw-prices">
                  <span className="bw-price-main">{t('bienestarHub.yogaPriceSingle')} <small>COP</small></span>
                  <span className="bw-price-alt">{t('bienestarHub.yogaPricePresencial')}</span>
                  <span className="bw-price-pack">{t('bienestarHub.yogaPricePack4')}</span>
                  <span className="bw-price-pack">{t('bienestarHub.yogaPricePack8')}</span>
                </div>
                <p className="bw-contact-note">{t('bienestarHub.contactSoon')}</p>
                <button type="button" className="bw-cta" onClick={() => goContact('yoga-1a-1')}>
                  {t('bienestarHub.yogaCta')}
                </button>
              </article>

              <article className="bw-session-card bw-session-card--training">
                <span className="bw-session-tag">Entrenamiento</span>
                <h3>{t('bienestarHub.trainingCardTitle')}</h3>
                <p className="bw-session-desc">{t('bienestarHub.trainingCardDesc')}</p>
                <ul className="bw-session-meta">
                  <li><i className="fas fa-stopwatch" /> {t('bienestarHub.trainingCardMeta1')}</li>
                  <li><i className="fas fa-calendar-day" /> {t('bienestarHub.trainingCardMeta2')}</li>
                  <li><i className="fas fa-bed" /> {t('bienestarHub.trainingCardMeta3')}</li>
                </ul>
                <div className="bw-prices">
                  <span className="bw-price-main">{t('bienestarHub.trainingPriceSingle')} <small>COP</small></span>
                  <span className="bw-price-pack">{t('bienestarHub.trainingPricePack4')}</span>
                  <span className="bw-price-pack">{t('bienestarHub.trainingPricePack8')}</span>
                </div>
                <p className="bw-contact-note">{t('bienestarHub.contactSoon')}</p>
                <button type="button" className="bw-cta" onClick={() => goContact('entrenamiento-1a-1')}>
                  {t('bienestarHub.trainingCta')}
                </button>
              </article>
            </div>
          </section>

          {/* Programas yoga */}
          <section id="programas-yoga" className="bw-section">
            <header className="bw-section-header">
              <h2 className="bw-section-title">{t('bienestarHub.programsTitle')}</h2>
              <p className="bw-section-sub">{t('bienestarHub.programsSub')}</p>
            </header>
            <div className="bw-program-grid">
              {YOGA_PROGRAMS.map((prog) => (
                <article
                  key={prog.id}
                  className="bw-program-card"
                  onClick={() => setSelectedProgram(prog)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedProgram(prog)}
                >
                  <span className="bw-program-badge">{t(`bienestarHub.${prog.badgeKey}`)}</span>
                  <h3>{t(`bienestarHub.${prog.titleKey}`)}</h3>
                  <p>{t(`bienestarHub.${prog.descKey}`)}</p>
                  <span className="bw-program-price">{t(`bienestarHub.${prog.priceKey}`)} COP</span>
                </article>
              ))}
            </div>
          </section>

          {/* Conecta */}
          <section id="programa-conecta" className="bw-section bw-conecta-block">
            <header className="bw-conecta-hero">
              <span className="bw-conecta-kicker">{t('bienestarHub.conectaKicker')}</span>
              <h2 className="bw-section-title bw-conecta-title">{t('bienestarHub.conectaTitle')}</h2>
              <p className="bw-section-sub">{t('bienestarHub.conectaLead')}</p>
            </header>

            <h3 className="bw-subheading">{t('bienestarHub.conectaForWhoTitle')}</h3>
            <div className="bw-two-col">
              <article className="bw-info-card">
                <div className="bw-info-icon"><i className="fas fa-brain" /></div>
                <h4>{t('bienestarHub.conectaPsyTitle')}</h4>
                <p>{t('bienestarHub.conectaPsyDesc')}</p>
              </article>
              <article className="bw-info-card">
                <div className="bw-info-icon"><i className="fas fa-route" /></div>
                <h4>{t('bienestarHub.conectaCoachTitle')}</h4>
                <p>{t('bienestarHub.conectaCoachDesc')}</p>
              </article>
            </div>

            <h3 className="bw-subheading">{t('bienestarHub.conectaProgramTitle')}</h3>
            <p className="bw-section-sub bw-section-sub--tight">{t('bienestarHub.conectaProgramSub')}</p>
            <div className="bw-conecta-sessions">
              {conectaSessions.map((s) => (
                <article key={s.n} className="bw-conecta-session-item">
                  <span className="bw-session-num">{s.n}</span>
                  <div>
                    <h4>{s.title}</h4>
                    <p>{s.desc}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="bw-pricing-row">
              <article className="bw-price-card">
                <span className="bw-price-label">Sesión única</span>
                <div className="bw-price-big">{t('bienestarHub.conectaPriceSingle')}<span>COP</span></div>
                <p>{t('bienestarHub.conectaPriceSingleDesc')}</p>
                <button type="button" className="bw-cta bw-cta--outline" onClick={() => goContact('conecta-sesion')}>
                  {t('bienestarHub.conectaPriceCtaSingle')}
                </button>
              </article>
              <article className="bw-price-card bw-price-card--featured">
                <span className="bw-price-label">Programa 7 sesiones</span>
                <div className="bw-price-big">{t('bienestarHub.conectaPriceProgram')}<span>COP</span></div>
                <p>{t('bienestarHub.conectaPriceProgramDesc')}</p>
                <button type="button" className="bw-cta" onClick={() => goContact('conecta-programa')}>
                  {t('bienestarHub.conectaPriceCtaProgram')}
                </button>
              </article>
            </div>

            <div className="bw-faq">
              <details>
                <summary>{t('bienestarHub.conectaFaqWho')}</summary>
                <p>{t('bienestarHub.conectaFaqWhoAnswer')}</p>
              </details>
              <details>
                <summary>{t('bienestarHub.conectaFaqPay')}</summary>
                <p>{t('bienestarHub.conectaFaqPayAnswer')}</p>
              </details>
              <details>
                <summary>{t('bienestarHub.conectaFaqCancel')}</summary>
                <p>{t('bienestarHub.conectaFaqCancelAnswer')}</p>
              </details>
              <details>
                <summary>{t('bienestarHub.conectaFaqClinical')}</summary>
                <p>{t('bienestarHub.conectaFaqClinicalAnswer')}</p>
              </details>
            </div>
          </section>

          {/* Aliados */}
          <section id="acompanamiento-aliados" className="bw-section bw-allies">
            <header className="bw-section-header">
              <h2 className="bw-section-title">{t('bienestarHub.alliesTitle')}</h2>
              <p className="bw-section-sub">{t('bienestarHub.alliesSub')}</p>
            </header>
            <button type="button" className="bw-cta" onClick={() => goContact('acompanamiento-aliado')}>
              {t('bienestarHub.alliesCta')}
            </button>
          </section>

          {/* Comunidad */}
          <section id="comunidad-deportiva" className="bw-section bw-community">
            <header className="bw-section-header">
              <h2 className="bw-section-title">{t('bienestarHub.communityTitle')}</h2>
              <p className="bw-section-sub">{t('bienestarHub.communitySub')}</p>
            </header>
            {communitySuccess ? (
              <p className="bw-success"><i className="fas fa-check-circle" /> {t('bienestarHub.communitySuccess')}</p>
            ) : (
              <form
                className="bw-waitlist-form"
                onSubmit={(e) => submitInterest(e, 'comunidad-deportiva', communityEmail, {
                  setSubmitting: setCommunitySubmitting,
                  setSuccess: setCommunitySuccess,
                  setError: setCommunityError,
                  setEmail: setCommunityEmail,
                })}
              >
                <input
                  type="email"
                  placeholder="Tu email"
                  value={communityEmail}
                  onChange={(e) => setCommunityEmail(e.target.value)}
                  required
                />
                <button type="submit" className="bw-cta" disabled={communitySubmitting}>
                  {communitySubmitting ? '...' : t('bienestarHub.communityCta')}
                </button>
                {communityError && <p className="bw-error">{communityError}</p>}
              </form>
            )}
          </section>

          {/* Agenda vacía */}
          <section id="agenda-del-mes" className="bw-section bw-agenda">
            <header className="bw-section-header">
              <h2 className="bw-section-title">{t('bienestarHub.agendaTitle')}</h2>
              <p className="bw-section-sub">{t('bienestarHub.agendaSub')}</p>
            </header>
            <div className="bw-agenda-empty">
              <i className="fas fa-calendar-alt" aria-hidden="true" />
              <p>{t('bienestarHub.agendaEmpty')}</p>
            </div>
          </section>
        </div>
      </main>

      {/* Modal programa yoga */}
      {selectedProgram && (
        <div className="event-modal-overlay" onClick={() => setSelectedProgram(null)}>
          <div className="event-modal javiland-course-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setSelectedProgram(null)} aria-label="Cerrar">
              <i className="fas fa-times" />
            </button>
            <div className="event-modal-content">
              <div className="event-modal-header">
                <span className="event-program-badge">{t(`bienestarHub.${selectedProgram.badgeKey}`)}</span>
                <h3 className="event-modal-title">{t(`bienestarHub.${selectedProgram.titleKey}`)}</h3>
                <p className="javiland-course-philosophy">{selectedProgram.philosophy}</p>
              </div>
              <div className="event-modal-body">
                <div className="event-description">
                  <h4>Descripción</h4>
                  <p>{selectedProgram.fullDesc}</p>
                  <p className="bw-program-price-modal">{t(`bienestarHub.${selectedProgram.priceKey}`)} COP</p>
                </div>
                <div className="javiland-interest-section">
                  <h4>{t('bienestarHub.programInterestNote')}</h4>
                  {interestSuccess ? (
                    <div className="interest-success">
                      <i className="fas fa-check-circle" /> Gracias. Te contactaremos pronto.
                    </div>
                  ) : (
                    <form
                      className="interest-form"
                      onSubmit={(e) => submitInterest(e, selectedProgram.id, interestEmail, {
                        setSubmitting: setInterestSubmitting,
                        setSuccess: setInterestSuccess,
                        setError: setInterestError,
                        setEmail: setInterestEmail,
                      })}
                    >
                      <div className="interest-form-row">
                        <input
                          type="email"
                          placeholder="Tu email"
                          value={interestEmail}
                          onChange={(e) => setInterestEmail(e.target.value)}
                          required
                        />
                        <button type="submit" className="interest-btn" disabled={interestSubmitting}>
                          {interestSubmitting ? '...' : t('bienestarHub.communityCta')}
                        </button>
                      </div>
                      {interestError && (
                        <div className="interest-error"><i className="fas fa-exclamation-circle" /> {interestError}</div>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BienestarPage;
